import React, { useState, useEffect, useRef } from 'react';
import Soundfont from 'soundfont-player';

const StringSounds = () => {
  const [playingStrings, setPlayingStrings] = useState([false, false, false, false]); // Track which strings are playing
  const audioContextRef = useRef(null);
  const violinRef = useRef(null);
  const playingRef = useRef([null, null, null, null]); // Store the active notes

  // Set up the AudioContext and load the instrument
  useEffect(() => {
    const init = async () => {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;

      // Load the violin instrument
      const violin = await Soundfont.instrument(audioContext, 'violin');
      violinRef.current = violin;
    };
    init();
  }, []);

  // Function to toggle playing the note for a string
  const toggleString = (index, note) => {
    const isPlaying = playingStrings[index];
    const updatedPlayingStrings = [false, false, false, false]; // Reset all strings

    // Stop all playing notes
    playingRef.current.forEach((player, i) => {
      if (player) {
        player.stop();
        playingRef.current[i] = null;
      }
    });

    if (!isPlaying) {
      // Play the new note if it was not already playing
      if (violinRef.current) {
        playingRef.current[index] = violinRef.current.play(note);
        updatedPlayingStrings[index] = true; // Mark the clicked string as playing
      }
    }

    setPlayingStrings(updatedPlayingStrings); // Update state
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <h2 style={styles.title}>Reference Tones</h2>
        <p style={styles.description}>
          Click on the buttons below to hear the reference pitch for each violin string.
          Use these tones to tune your instrument or practice intonation.
        </p>
      </div>

      <div style={styles.stringsContainer}>
        {/* String G (lowest) */}
        <div style={styles.stringGroup}>
          <div style={styles.stringLabel}>G String</div>
          <button
            style={playingStrings[3] ? styles.playingButton : styles.button}
            onClick={() => toggleString(3, 'G3')}
            aria-label="Play G string reference tone"
          >
            G
          </button>
        </div>

        {/* String D */}
        <div style={styles.stringGroup}>
          <div style={styles.stringLabel}>D String</div>
          <button
            style={playingStrings[2] ? styles.playingButton : styles.button}
            onClick={() => toggleString(2, 'D4')}
            aria-label="Play D string reference tone"
          >
            D
          </button>
        </div>

        {/* String A */}
        <div style={styles.stringGroup}>
          <div style={styles.stringLabel}>A String</div>
          <button
            style={playingStrings[1] ? styles.playingButton : styles.button}
            onClick={() => toggleString(1, 'A4')}
            aria-label="Play A string reference tone"
          >
            A
          </button>
        </div>

        {/* String E (highest) */}
        <div style={styles.stringGroup}>
          <div style={styles.stringLabel}>E String</div>
          <button
            style={playingStrings[0] ? styles.playingButton : styles.button}
            onClick={() => toggleString(0, 'E5')}
            aria-label="Play E string reference tone"
          >
            E
          </button>
        </div>
      </div>
    </div>
  );
};

export default StringSounds;

// Styles matching the app design system
const styles = {
  card: {
    padding: 'clamp(24px, 4vw, 32px)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--surface-border)',
    background: 'var(--surface)',
    boxShadow: 'var(--shadow-lg)',
    marginBottom: '32px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    margin: '0 0 8px 0',
  },
  description: {
    fontSize: '16px',
    color: 'var(--text-secondary)',
    lineHeight: '1.5',
    margin: 0,
    maxWidth: '600px',
    margin: '0 auto',
  },
  stringsContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: 'clamp(16px, 3vw, 24px)',
    flexWrap: 'wrap',
  },
  stringGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  stringLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--text-secondary)',
    textAlign: 'center',
  },
  button: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    border: '2px solid var(--accent)',
    backgroundColor: 'var(--surface)',
    color: 'var(--accent)',
    fontSize: '24px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all 0.2s ease',
    boxShadow: 'var(--shadow-md)',
  },
  playingButton: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    border: '2px solid var(--accent-strong)',
    backgroundColor: 'var(--accent-soft)',
    color: 'var(--accent-strong)',
    fontSize: '24px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all 0.2s ease',
    boxShadow: 'var(--shadow-md)',
    transform: 'scale(1.05)',
  },
};